import { ReceiptLongOutlined } from "@mui/icons-material";
import { Alert, Box, Chip, IconButton, Snackbar, Tooltip } from "@mui/material";
import { type ReactNode, useState } from "react";
import {
  fetchGroupTransactions,
  fetchOrgTransactions,
  fetchReceiptDocumentUrl,
} from "@/api/invoices";
import Blotter, {
  type BlotterColumn,
  type BlotterConfig,
  type BlotterData,
} from "@/components/Blotter";
import GroupTransfersPanel from "@/components/usersOrgs/GroupTransfersPanel";
import { useGroupTransactions, useOrgTransactions } from "@/hooks/useInvoices";
import type { PaymentReceipt, ReceiptListQuery } from "@/interfaces/invoices";
import { RECEIPT_KIND, RECEIPT_METHOD_LABEL, RECEIPT_SOURCE } from "@/interfaces/invoices";

const money = (v: string | number | null | undefined, ccy = "usd") =>
  v == null
    ? "—"
    : `${ccy.toUpperCase()} ${Number(v).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
const dateTime = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const sourceLabel = (r: PaymentReceipt) =>
  r.sourceType === RECEIPT_SOURCE.invoice ? `Invoice #${r.sourceId}` : "Credit purchase";
function kindBadge(r: PaymentReceipt): { label: string; color: "default" | "success" | "warning" } {
  if (r.reversesReceiptId != null) return { label: "Reversal", color: "default" };
  if (r.kind === RECEIPT_KIND.write_off) return { label: "Write-off", color: "warning" };
  return { label: "Payment", color: "success" };
}

/**
 * Rows with a Stripe document behind them: money that arrived. Write-offs and reversal rows record
 * none. A card purchase opens its charge receipt; an invoice payment opens the invoice's hosted
 * page, which carries the receipt download once paid.
 */
const hasDocument = (r: PaymentReceipt) =>
  r.kind === RECEIPT_KIND.payment && r.reversesReceiptId == null && Number(r.amount) > 0;

/**
 * The group's Transactions tab, in two lists.
 *
 * PURCHASES is the money-in feed — buy-credits, invoice payments, write-offs, reversals — in
 * dollars, server-paginated, never loading the whole ledger.
 *
 * TRANSFERS is credits changing hands between groups of one organisation. Kept apart rather than
 * merged because the two are different ledgers: one is money arriving from a client, the other is
 * credits already paid for moving inside it. One table would need an amount column meaning two
 * things and a date column answering to two clocks.
 */
export default function GroupTransactionsPanel({ groupId }: { groupId: number }) {
  return (
    <TransactionsTabs
      purchases={<PurchasesBlotter source={{ groupId }} />}
      transfers={<GroupTransfersPanel groupId={groupId} />}
    />
  );
}

/**
 * An organization's Transactions tab: every group's purchases in one list, so a client finds all
 * of their receipts in one place whatever their plan. Transfers are a per-group ledger, so they
 * appear only when the organization is shown as its one root group (`transfersGroupId`);
 * with the tree showing they stay on each group's own tab.
 */
export function OrgTransactionsPanel({
  orgId,
  transfersGroupId,
  showGroup,
}: {
  orgId: number;
  transfersGroupId: number | null;
  /** Adds a Group column — only where the client already sees groups. */
  showGroup: boolean;
}) {
  const purchases = <PurchasesBlotter source={{ orgId }} showGroup={showGroup} />;
  if (transfersGroupId == null) return purchases;
  return (
    <TransactionsTabs
      purchases={purchases}
      transfers={<GroupTransfersPanel groupId={transfersGroupId} />}
    />
  );
}

function TransactionsTabs({
  purchases,
  transfers,
}: {
  purchases: ReactNode;
  transfers: ReactNode;
}) {
  const [view, setView] = useState<"purchases" | "transfers">("purchases");

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          borderBottom: "1px solid var(--color-card-border)",
          mb: 3,
        }}
      >
        {(
          [
            ["purchases", "Purchases"],
            ["transfers", "Transfers"],
          ] as const
        ).map(([key, label]) => (
          <Box
            component="button"
            type="button"
            key={key}
            onClick={() => setView(key)}
            aria-current={view === key ? "page" : undefined}
            sx={{
              background: "none",
              border: 0,
              cursor: "pointer",
              px: 0,
              pb: 1.25,
              fontSize: 14,
              fontWeight: view === key ? 700 : 500,
              color: view === key ? "var(--color-dark-dust)" : "text.secondary",
              borderBottom: "2px solid",
              borderColor: view === key ? "var(--color-dark-dust)" : "transparent",
            }}
          >
            {label}
          </Box>
        ))}
      </Box>

      {view === "purchases" ? purchases : transfers}
    </Box>
  );
}

type FeedSource = { groupId: number } | { orgId: number };

function PurchasesBlotter({
  source,
  showGroup = false,
}: {
  source: FeedSource;
  showGroup?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);

  // One of the two is chosen per mount (`source` doesn't change kind), so the Blotter still calls
  // one hook, unconditionally, on every render.
  const useGroupData = (q: ReceiptListQuery): BlotterData<PaymentReceipt> =>
    toBlotterData(useGroupTransactions("groupId" in source ? source.groupId : null, feedQuery(q)));
  const useOrgData = (q: ReceiptListQuery): BlotterData<PaymentReceipt> =>
    toBlotterData(useOrgTransactions("orgId" in source ? source.orgId : 0, feedQuery(q)));
  const useData = "orgId" in source ? useOrgData : useGroupData;
  const fetchPage = (q: ReceiptListQuery) =>
    "orgId" in source
      ? fetchOrgTransactions(source.orgId, q)
      : fetchGroupTransactions(source.groupId, q);

  /**
   * The tab is opened inside the click, before the request: a window opened after an await is no
   * longer the user's gesture and the browser blocks it. It is pointed at Stripe once the URL
   * arrives, and closed again if there is none.
   */
  const openDocument = async (r: PaymentReceipt) => {
    const win = window.open("", "_blank");
    try {
      const url = await fetchReceiptDocumentUrl(r.groupId, r.id);
      if (win) {
        win.opener = null;
        win.location.href = url;
      } else {
        window.location.href = url;
      }
    } catch (e) {
      win?.close();
      setError(e instanceof Error ? e.message : "Couldn't open the receipt");
    }
  };

  const groupColumn: BlotterColumn<PaymentReceipt> = {
    key: "group",
    label: "Group",
    render: (r) => r.groupName ?? "—",
    exportValue: (r) => r.groupName ?? "",
  };
  const documentColumn: BlotterColumn<PaymentReceipt> = {
    key: "document",
    label: "Receipt",
    render: (r) =>
      hasDocument(r) ? (
        <Tooltip
          title={r.sourceType === RECEIPT_SOURCE.invoice ? "Open invoice" : "Open receipt"}
          arrow
        >
          <IconButton
            size="small"
            aria-label="Open receipt"
            onClick={(e) => {
              e.stopPropagation();
              void openDocument(r);
            }}
          >
            <ReceiptLongOutlined sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      ) : null,
  };

  const dataColumns: BlotterColumn<PaymentReceipt>[] = [
    {
      key: "received",
      label: "Date",
      sortable: true,
      sortValue: (r) => r.receivedAt,
      render: (r) => dateTime(r.receivedAt),
    },
    ...(showGroup ? [groupColumn] : []),
    { key: "source", label: "Source", render: sourceLabel, exportValue: sourceLabel },
    {
      key: "kind",
      label: "Type",
      sortable: true,
      sortValue: (r) => r.kind,
      render: (r) => {
        const b = kindBadge(r);
        return (
          <Chip
            size="small"
            label={b.label}
            color={b.color}
            variant="outlined"
            sx={{ height: 20 }}
          />
        );
      },
      exportValue: (r) => kindBadge(r).label,
    },
    {
      key: "amount",
      label: "Amount",
      sortable: true,
      sortValue: (r) => Number(r.amount),
      render: (r) => (
        <Box
          component="span"
          sx={{ color: Number(r.amount) < 0 ? "var(--color-red, #c00)" : "inherit" }}
        >
          {money(r.amount, r.currency)}
        </Box>
      ),
      exportValue: (r) => money(r.amount, r.currency),
    },
    {
      key: "method",
      label: "Method",
      render: (r) => (r.method != null ? (RECEIPT_METHOD_LABEL[r.method] ?? "—") : "—"),
      exportValue: (r) => (r.method != null ? (RECEIPT_METHOD_LABEL[r.method] ?? "") : ""),
    },
    { key: "reference", label: "Reference", render: (r) => r.reference ?? "—" },
    { key: "note", label: "Note", render: (r) => r.note ?? "—" },
  ];

  const config: BlotterConfig<PaymentReceipt> = {
    title: "Purchases",
    searchPlaceholder: "Search purchases",
    itemNoun: "purchase",
    rowKey: (r) => r.id,
    defaultSortKey: "received",
    defaultSortDir: "desc",
    filterFields: [],
    useData,
    exportFilename: "purchases.csv",
    exportFetch: async (q) => {
      const res = await fetchPage({ page: q.page, limit: q.limit });
      return { data: res.data, totalPages: res.totalPages };
    },
    columns: [...dataColumns, documentColumn],
    // The receipt column is a link, not data — left out of the CSV.
    csvColumns: dataColumns.map((c) => ({
      label: c.label,
      value: (row) => c.exportValue?.(row) ?? String(c.sortValue?.(row) ?? ""),
    })),
  };

  return (
    <>
      <Blotter config={config} />
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setError(null)} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}

/** The Blotter's query state narrowed to what the feed takes (it has no filters). */
const feedQuery = (q: ReceiptListQuery): ReceiptListQuery => ({
  search: q.search || undefined,
  sortBy: q.sortBy,
  sortDir: q.sortDir,
  page: q.page,
  limit: q.limit,
});

function toBlotterData(result: {
  data?: { data: PaymentReceipt[]; total: number; totalPages: number };
  isLoading: boolean;
}): BlotterData<PaymentReceipt> {
  return {
    data: result.data?.data ?? [],
    total: result.data?.total ?? 0,
    totalPages: result.data?.totalPages ?? 1,
    isLoading: result.isLoading,
  };
}
